'use client';

import { useMemo, useState } from 'react';
import { EditModal } from '@/components/ui/edit-modal';
import { SignupDeadlineField } from '@/features/tournament-wizard/blocks';
import { formatDateForUser } from '@/utils/formatters';
import { toLocalMidnightDate } from '@/utils/localDateOnly';
import {
  latestSignupDeadlineDay,
  isSignupDeadlineTooLate,
} from '@/utils/signupDeadline';

interface EditSignupDeadlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDeadline: string;
  /** Tournament start, used as the upper bound: the deadline lands the day before at the latest. */
  startDate: string;
  onSave: (newDeadline: Date) => Promise<boolean>;
  isLoading: boolean;
}

/**
 * Standalone editor for the signup deadline, reachable while the tournament has no fixture — even
 * with teams already registered. `EditDatesAndScheduleModal` also carries this field, but it is a
 * two-step flow that refuses to save without re-picking schedules, so it is the wrong door for
 * "move the closing time by two hours".
 */
export function EditSignupDeadlineModal({
  isOpen,
  onClose,
  currentDeadline,
  startDate,
  onSave,
  isLoading,
}: EditSignupDeadlineModalProps) {
  const [deadline, setDeadline] = useState<Date | null>(
    currentDeadline ? new Date(currentDeadline) : null,
  );

  // Today, not tomorrow: closing "today at 10:00" when it is already 15:00 is how the manager
  // shuts registration down right now, so the wizard's "at least one day away" floor is wrong here.
  const minDate = useMemo(() => toLocalMidnightDate(new Date()) ?? undefined, []);

  const maxDate = useMemo(
    () => latestSignupDeadlineDay(startDate) ?? undefined,
    [startDate],
  );

  const error = useMemo(() => {
    if (!deadline) return 'Elegí una fecha y hora de cierre.';
    if (isSignupDeadlineTooLate(deadline, startDate)) {
      return `El cierre debe ser al menos un día antes del inicio del torneo (${formatDateForUser(startDate)}).`;
    }
    return null;
  }, [deadline, startDate]);

  // The save button is already disabled while `error` is set, so this only narrows the type.
  const handleSave = async () => {
    if (!deadline) return;
    await onSave(deadline);
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar cierre de inscripción"
      onSave={handleSave}
      isLoading={isLoading}
      disabled={!!error}
    >
      <div className="space-y-3 py-4">
        <SignupDeadlineField
          value={deadline}
          onChange={setDeadline}
          calendarMinDate={minDate}
          calendarMaxDate={maxDate}
          wrapInCard={false}
          inputId="edit-tournament-signup-deadline-only"
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </EditModal>
  );
}
